import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ButtonComponent } from '@examdojo/core/button';
import { TimeAgoModule } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionValidationHeaderComponent } from '@examdojo/question';
import {
  ConsensusFeedbackFormComponent,
  PreviousEvaluationsComponent,
  QuestionReviewTypeBadgeComponent,
  ReviewItemQuery,
  ReviewSolutionCaptureDialogService,
  ReviewSolutionImageQuery,
  ReviewSolutionImageService,
} from '@examdojo/review';
import { UserQuery } from '@examdojo/user';
import { EMPTY, filter, finalize, map, tap } from 'rxjs';
import { ReviewModeBaseComponent } from '../review-mode-base.component';

@Component({
  selector: 'dojo-human-review-sidebar',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    QuestionValidationHeaderComponent,
    ConsensusFeedbackFormComponent,
    MatTabsModule,
    MatTooltipModule,
    TimeAgoModule,
    MatExpansionModule,
    QuestionReviewTypeBadgeComponent,
    PreviousEvaluationsComponent,
  ],
  templateUrl: './human-review-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full flex-col overflow-y-auto' },
  styles: [
    `
      ::ng-deep .mat-mdc-tab-labels {
        height: 45px;
      }
    `,
  ],
})
export class HumanReviewSidebarComponent extends ReviewModeBaseComponent {
  constructor(
    private readonly localUserQuery: UserQuery,
    private readonly reviewSolutionCaptureDialogService: ReviewSolutionCaptureDialogService,
    private readonly reviewSolutionImageQuery: ReviewSolutionImageQuery,
    private readonly reviewItemQuery: ReviewItemQuery,
    private readonly reviewSolutionImageService: ReviewSolutionImageService,
    protected override readonly errorHandlerService: ErrorHandlerService,
    protected override readonly destroyRef: DestroyRef,
  ) {
    super();

    this.reviewItemQuery
      .selectProp('session')
      .pipe(
        filter(isNotNullish),
        tap((session) => this.refreshSolutionImages(session)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly isRefreshing = signal(false);

  readonly uploadedImagesCount = toSignal(
    this.reviewSolutionImageQuery.entities$.pipe(map((images) => images.length)),
    { initialValue: 0 },
  );

  async openUploadDialog() {
    const userId = this.localUserQuery.getActiveId();
    assertNonNullable(userId, 'userId');

    const reviewSession = this.reviewItemQuery.getProp('session');
    assertNonNullable(reviewSession, 'reviewSession');

    await this.reviewSolutionCaptureDialogService.openDialog({
      input: {
        userId,
        reviewSession,
      },
    });
  }

  refreshSolutionImages(reviewSession: string | undefined = this.reviewItemQuery.getProp('session')) {
    if (!reviewSession) {
      return;
    }

    this.isRefreshing.set(true);

    this.reviewSolutionImageService
      .fetchAll(reviewSession)
      .pipe(
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        finalize(() => this.isRefreshing.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
